import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { Message, MessageStats } from '../types';
import { sortMessagesByDate } from '../utils';

const messagesApi = {
    async getMessages(): Promise<Message[]> {
        await new Promise(resolve => setTimeout(resolve, 300));
        
        return [
            {
                id: '1',
                kind: 'critical',
                title: 'Магазин перестал работать',
                body: 'shop.example.ru недоступен · сервер не отвечает 14 минут',
                timestamp: new Date(Date.now() - 14 * 60000).toISOString(),
                status: 'unread',
                metadata: { siteId: 'shop', siteName: 'shop.example.ru', duration: 14 },
            },
            {
                id: '2',
                kind: 'warning',
                title: 'Личный кабинет работает медленно',
                body: 'lk.example.ru · время отклика 1.2 сек · норма: 300 мс',
                timestamp: new Date(Date.now() - 30 * 60000).toISOString(),
                status: 'unread',
                metadata: { siteId: 'lk', siteName: 'lk.example.ru', duration: 45 },
            },
            {
                id: '3',
                kind: 'info',
                title: 'Еженедельный отчёт готов',
                body: 'Статистика работы всех сайтов за прошлую неделю доступна для просмотра',
                timestamp: new Date(Date.now() - 24 * 3600000).toISOString(),
                status: 'unread',
            },
            {
                id: '4',
                kind: 'ok',
                title: 'API восстановлен после сбоя',
                body: 'api.example.ru · сбой устранён, сайт отвечает нормально · простой 3 мин',
                timestamp: new Date(Date.now() - 2 * 24 * 3600000).toISOString(),
                status: 'read',
                metadata: { siteId: 'api', siteName: 'api.example.ru', duration: 3, resolvedAt: new Date().toISOString() },
            },
            {
                id: '5',
                kind: 'resolved',
                title: 'Сертификат SSL продлён',
                body: 'example.ru · сертификат успешно продлён до 15 марта 2027',
                timestamp: new Date(Date.now() - 5 * 24 * 3600000).toISOString(),
                status: 'read',
            },
        ];
    },

    async markAsRead(id: string): Promise<void> {
        await new Promise(resolve => setTimeout(resolve, 100));
        console.log(`Marked ${id} as read`);
    },

    async markAllAsRead(): Promise<void> {
        await new Promise(resolve => setTimeout(resolve, 200));
        console.log('Marked all as read');
    },

    async archiveMessage(id: string): Promise<void> {
        await new Promise(resolve => setTimeout(resolve, 100));
        console.log(`Archived ${id}`);
    },
};

const QUERY_KEY = ['messages'] as const;

export function useMessages() {
    const queryClient = useQueryClient();

    const { data: messages = [], isLoading, error, refetch } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: messagesApi.getMessages,
        staleTime: 30000,
        refetchInterval: 30000,
    });

    const markAsReadMutation = useMutation({
        mutationFn: messagesApi.markAsRead,
        onMutate: async (id: string) => {
        await queryClient.cancelQueries({ queryKey: QUERY_KEY });
        
        const previousMessages = queryClient.getQueryData<Message[]>(QUERY_KEY);
        
        queryClient.setQueryData<Message[]>(QUERY_KEY, old => 
            old?.map(msg => msg.id === id ? { ...msg, status: 'read' as const } : msg) || []
        );
        
        return { previousMessages };
        },
        onError: (_, __, context) => {
            if (context?.previousMessages) {
                queryClient.setQueryData(QUERY_KEY, context.previousMessages);
            }
        },
    });

    const markAllAsReadMutation = useMutation({
        mutationFn: messagesApi.markAllAsRead,
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: QUERY_KEY });
            
            const previousMessages = queryClient.getQueryData<Message[]>(QUERY_KEY);
            
            queryClient.setQueryData<Message[]>(QUERY_KEY, old =>
                old?.map(msg => ({ ...msg, status: 'read' as const })) || []
            );
            
            return { previousMessages };
        },
        onError: (_, __, context) => {
            if (context?.previousMessages) {
                queryClient.setQueryData(QUERY_KEY, context.previousMessages);
            }
        },
    });

    const archiveMutation = useMutation({
        mutationFn: messagesApi.archiveMessage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });

    const sortedMessages = useMemo(() => sortMessagesByDate(messages), [messages]);

    const stats: MessageStats = useMemo(() => ({
        total: messages.length,
        unread: messages.filter(m => m.status === 'unread').length,
        critical: messages.filter(m => m.kind === 'critical' && m.status === 'unread').length,
        warning: messages.filter(m => m.kind === 'warning' && m.status === 'unread').length,
    }), [messages]);

    const markAsRead = useCallback((id: string) => {
        markAsReadMutation.mutate(id);
    }, [markAsReadMutation]);

    const markAllAsRead = useCallback(() => {
        markAllAsReadMutation.mutate();
    }, [markAllAsReadMutation]);

    const archiveMessage = useCallback((id: string) => {
        archiveMutation.mutate(id);
    }, [archiveMutation]);

    return {
        messages: sortedMessages,
        stats,
        isLoading,
        error,
        refetch,
        markAsRead,
        markAllAsRead,
        archiveMessage,
        isMarkingAsRead: markAsReadMutation.isPending,
        isMarkingAllAsRead: markAllAsReadMutation.isPending,
    };
}